import { EChartsOption } from 'echarts';
import { getMappedColor } from '../helpers/getColor';
import {
    CartesianChartOption,
    CartesianChartType,
} from './CartesianChartOption';
import { EChartsSerie, SerieLabel } from './types';
import { formatDisplay } from 'helpers/formatHelper';
import { getVisualMapSettings } from 'helpers/chart/visualMap';
import { getValidSettings, plainResponseToFloatValue } from './helpers';
import {
    ChartSettingsT,
    OrderedColumnsT,
    TimeScales,
    SlidingTimeScales,
} from 'business/definitions';
import { barSettings } from 'routes/Explore/components/Settings/components/ChartSettingsDrawer/helpers/settingsByViz/bar';
import { isDateOrYear, isTypeNumeric } from 'helpers/helpers';
import { EChartAxisType, SeriePointT } from '../../types';
import { formatTooltipTitle, getTooltipString } from 'helpers/chartHelper';

export class BarChartOption extends CartesianChartOption {
    constructor(
        orderedColumns: OrderedColumnsT,
        settings: ChartSettingsT,
        data: ResponseDataT[],
        width: number,
        height: number,
        dataZoomInterval: [number, number] | undefined,
        isPdfModeActivated: boolean,
        isPngModeActivated: boolean,
        isChartViewedOnDashboard: boolean,
    ) {
        // Delete unhandled settings:
        const properSettings = getValidSettings(settings, barSettings);
        super(
            orderedColumns,
            properSettings,
            data,
            width,
            height,
            dataZoomInterval,
            isPdfModeActivated,
            isPngModeActivated,
            isChartViewedOnDashboard,
        );
    }

    protected getChartType(): CartesianChartType {
        return 'bar';
    }

    getChartOption(): EChartsOption {
        const option = super.getChartOption();
        if (this.settings.useGradientColor) {
            const visualMap = this.getVisualMap();
            // When we show the visual map, we hide the legend.
            return {
                ...option,
                visualMap,
                legend: {
                    ...option.legend,
                    show: false,
                },
            };
        }
        return option;
    }

    protected override getLabel(
        serie: EChartsSerie,
        serieIndex: number,
    ): SerieLabel {
        let label = { ...super.getLabel(serie, serieIndex), position: 'top' };

        // Special styling for when the label are supposed to be shown inside the bar.
        // This also concern stacked bars.
        // We disable it for the double axis, if activated, because it is shown as a
        // separate line.
        const shouldShowInsideStyle =
            this.settings.isStackedBars || this.settings.areLabelsShownInside;
        const isDoubleAxisSerie =
            this.settings.secondaryYAxis !== undefined &&
            this.settings.secondaryYAxis === serie.id;
        const useAlternativeStyle = shouldShowInsideStyle && !isDoubleAxisSerie;
        if (useAlternativeStyle) {
            label = {
                ...label,
                color: 'white',
                textBorderColor: getMappedColor(
                    serie.name,
                    serieIndex,
                    this.getCustomColor(serie.name),
                    this.getColor(),
                ),
                textBorderWidth: 4,
                position: 'inside',
                align: 'center',
            };
        }

        // When the labels are shown inside and vertical (yes the boolean is flipped
        // this is a known issue), we want to add an offset and align the label to the
        // left.
        if (useAlternativeStyle && !this.settings.areLabelsVertical) {
            label = {
                ...label,
                // @ts-expect-error [typing] it is a valid option, @see https://echarts.apache.org/en/option.html#series-bar.data.label.offset
                offset: [3, 0],
                align: 'left',
            };
        }

        // @ts-expect-error [typing]
        return label;
    }

    protected override getSeries(): EChartsSerie[] {
        let series = super.getSeries();

        series = series.map((serie, serieIndex) => ({
            ...serie,
            barMaxWidth: 48,
            barGap: 0.3,
            itemStyle: {
                borderRadius: 4,
                color: getMappedColor(
                    serie.name,
                    serieIndex,
                    this.getCustomColor(serie.name),
                    this.getColor(),
                ),
            },
        }));

        if (this.settings.isStackedBars) {
            series = series.map((serie) => ({
                ...serie,
                // @ts-expect-error [typing] echarts think this is a treeserie for some reason.
                stack: serie.encode.x,
                itemStyle: {
                    // @ts-expect-error [typing]
                    ...serie.itemStyle,
                    borderRadius: 0,
                },
            }));
        }

        return series;
    }

    protected getMinMaxValue(): { min: number; max: number } {
        const valueKey = this.orderedColumns.y.key;
        let maxValue = 0;
        let minValue = Number.MAX_VALUE;
        for (const dataset of this.dataset) {
            if (!Object.hasOwn(dataset, 'source')) {
                continue;
            }
            for (const data of dataset.source) {
                const yValue = data[valueKey];
                if (!yValue) {
                    continue;
                }
                const numberValue: number = plainResponseToFloatValue(yValue);
                if (numberValue > maxValue) {
                    maxValue = numberValue;
                }
                if (numberValue < minValue) {
                    minValue = numberValue;
                }
            }
        }
        return { max: maxValue, min: minValue };
    }

    protected getVisualMap() {
        const { min, max } = this.getMinMaxValue();
        return getVisualMapSettings({
            maxValue:
                this.settings.goalValue !== undefined
                    ? this.settings.goalValue
                    : max,
            minValue: min,
            useGradientColor: !!this.settings.useGradientColor,
            reverseGradientColor: !!this.settings.reverseGradientColor,
            showLegend: !!this.settings.showLegend,
            showLegendOnRightSide: !!this.settings.showLegendOnRightSide,
            palette: this.settings.palette || 'manty',
            dimensionKey: this.orderedColumns.y.key,
            useDataset: true,
            formatter: (value: number) =>
                formatDisplay(
                    value,
                    this.orderedColumns.y.type,
                    this.getDecimalPrecision(),
                    this.settings.displayBy,
                ),
        });
    }

    protected override getXAxisType(): EChartAxisType {
        const isANumericInterval =
            isTypeNumeric(this.orderedColumns.x!.type) && this.interval;
        const isDiscreteScale =
            this.orderedColumns.x?.scale &&
            [
                TimeScales.schoolYear,
                SlidingTimeScales.slidingWeek,
                SlidingTimeScales.slidingYear,
            ].includes(this.orderedColumns.x.scale);

        if (
            this.settings.isStackedBars ||
            isANumericInterval ||
            isDiscreteScale
        ) {
            return 'category';
        }
        return super.getXAxisType();
    }

    protected override getFormattedSeriesValues(params: SeriePointT[]) {
        const value = params[0].axisValue;
        let title = formatTooltipTitle(
            value,
            this.orderedColumns.x!.type,
            this.orderedColumns.x?.scale,
            this.interval,
            this.treeValueTranslations,
        ).toString();

        // For stacked bars, we also display the sum of all series in the bar.
        if (params.length > 1 && this.settings.isStackedBars) {
            const dimension = params[0].dimensionNames[params[0].encode.y[0]];
            const sum = params
                .map((d) => Number(d.value[dimension]))
                .filter((d) => d) // filter NaN, 0 and undefined
                .reduce((acc, d) => acc + d, 0);
            title = `${title} : ${formatDisplay(
                sum,
                this.orderedColumns.y.type,
                this.getDecimalPrecision(),
                this.settings.displayBy,
            )}`;
        }

        // We only display the evolution % when the X axis is a time
        // and when we are not doing a pluri-annual comparision.
        const isYearlyComparision = this.settings.isYearlyComparison ?? false;
        const showEvolution =
            isDateOrYear(this.orderedColumns.x?.type) && !isYearlyComparision;
        const tooltipSeries = params.map((d) =>
            this.getFormattedSerieValue(d, showEvolution),
        );
        return getTooltipString(title.toString(), tooltipSeries);
    }
}
